import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Bell,
  Check,
  CheckCheck,
  AlertCircle,
  Info,
  AlertTriangle,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'alert':
        return <AlertCircle className="w-5 h-5 text-rose-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case 'alert':
        return 'bg-rose-500/10';
      case 'warning':
        return 'bg-amber-500/10';
      case 'success':
        return 'bg-emerald-500/10';
      case 'info':
      default:
        return 'bg-blue-500/10';
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            Notifications
          </h1>
          <p className="text-zinc-400 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            className="border-zinc-700"
            onClick={markAllAsRead}
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-4 h-4 mr-2" />
            Mark all as read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-0">
          {notifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold text-white mb-2">No notifications</h3>
              <p className="text-zinc-400">You're all caught up! Check back later.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 p-4 hover:bg-zinc-800/30 transition-colors cursor-pointer ${
                    !notification.read ? 'bg-blue-500/5' : ''
                  }`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className={`w-10 h-10 rounded-full ${getNotificationBg(notification.type)} flex items-center justify-center flex-shrink-0`}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`font-medium ${notification.read ? 'text-zinc-300' : 'text-white'}`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-zinc-500 mt-0.5">{notification.message}</p>
                      </div>
                      {!notification.read && (
                        <Badge className="bg-blue-500/20 text-blue-400 border-0 text-[10px]">NEW</Badge>
                      )}
                    </div>
                    <p className="text-xs text-zinc-600 mt-2">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sample notifications info */}
      {notifications.length === 0 && (
        <div className="glass-card rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
            About Notifications
          </h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            You'll receive notifications for important events such as:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-zinc-400">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              Campaign status changes (activated, paused, completed)
            </li>
            <li className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500" />
              Screen offline alerts
            </li>
            <li className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Billing reminders and overdue invoices
            </li>
            <li className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-500" />
              System updates and new features
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
